/**
 * City grid, mech allocation, construction and demolition —
 * spec §9, §10, §8 steps 7-8.
 *
 * Mechs are a fungible pool, not individuals. Every active plot draws one, and
 * surplus mechs spread across the active plots to accelerate them. The pool size
 * is therefore the cap on how many plots can be under construction at once,
 * which is what makes the late game mech-gated rather than materials-gated.
 */

import {
  BASE_BUILD_TICKS,
  BUILD_DISTANCE_PENALTY_PER_PLOT,
  CITY_GRID_SIZE,
  FACTORY_MAX,
  FACTORY_MIN,
} from './constants';
import { BUILDINGS, GOOD_IDS } from './data';
import type { GameState, Plot, PlotCoord } from './types';

/** The centre 3x3 block. Not buildable, not demolishable. spec §3 */
export function isFactoryPlot(coord: PlotCoord): boolean {
  return (
    coord.x >= FACTORY_MIN &&
    coord.x <= FACTORY_MAX &&
    coord.y >= FACTORY_MIN &&
    coord.y <= FACTORY_MAX
  );
}

export function inBounds(coord: PlotCoord): boolean {
  return (
    coord.x >= 0 && coord.x < CITY_GRID_SIZE && coord.y >= 0 && coord.y < CITY_GRID_SIZE
  );
}

export function plotAt(state: GameState, coord: PlotCoord): Plot | null {
  if (!inBounds(coord)) return null;
  return state.city.grid[coord.y]?.[coord.x] ?? null;
}

/** A plot is available if it is in bounds, not the factory, and holds nothing. */
export function isBuildable(state: GameState, coord: PlotCoord): boolean {
  return inBounds(coord) && !isFactoryPlot(coord) && plotAt(state, coord) === null;
}

/**
 * Orthogonal neighbours. Four-way, not eight.
 *
 * Diagonal adjacency would not produce a connected road network: two plots
 * touching only at a corner have no traversable route between them when the
 * two orthogonal cells between them are empty. Since the whole point of the
 * adjacency rule is that mechs can always reach a site without pathfinding
 * (spec §9), the connectivity metric has to be the one roads actually use.
 *
 * Note this is a different metric from the Chebyshev distance used for build
 * time above — deliberately. That one approximates travel distance; this one
 * defines connectivity.
 */
function orthogonalNeighbours(coord: PlotCoord): PlotCoord[] {
  return [
    { x: coord.x + 1, y: coord.y },
    { x: coord.x - 1, y: coord.y },
    { x: coord.x, y: coord.y + 1 },
    { x: coord.x, y: coord.y - 1 },
  ];
}

/**
 * THE ADJACENCY RULE (spec §9).
 *
 * Spec §9 invokes "the adjacency rule" and "construction adjacency" twice as
 * established fact — it is what guarantees "mechs cannot fail to reach a site
 * and no pathfinding is needed" — but never states it. Without it a plot in the
 * far corner builds with nothing between it and the factory, which is precisely
 * the situation the no-pathfinding claim assumes cannot arise.
 *
 * Stated here: a plot may be built only if it orthogonally touches the factory
 * block or an existing structure. The city therefore grows outward from the
 * factory as a single connected mass. See SPEC-ERRATA.md E12.
 *
 * Two variants, because "planned" and "reachable" are not the same thing.
 * This one accepts any occupied neighbour, including merely queued ones, so a
 * contiguous plan can be laid out in advance.
 */
export function isConnectedForPlanning(state: GameState, coord: PlotCoord): boolean {
  return orthogonalNeighbours(coord).some(
    (n) => isFactoryPlot(n) || (inBounds(n) && plotAt(state, n) !== null),
  );
}

/**
 * The stricter variant, checked when a build actually starts.
 *
 * A queued neighbour is a plan, not a road — nothing has been built there. If
 * planning-adjacency were enough at start time, cancelling the plot that
 * connected a chain to the factory would leave the rest of the chain
 * "connected" to each other and buildable in mid-air.
 */
export function isReachable(state: GameState, coord: PlotCoord): boolean {
  return orthogonalNeighbours(coord).some((n) => {
    if (isFactoryPlot(n)) return true;
    const neighbour = plotAt(state, n);
    return neighbour !== null && neighbour.state !== 'queued';
  });
}

/**
 * Chebyshev distance from the factory block's edge.
 *
 * Spec §9 says "distance from the factory edge" and then states the grid corner
 * costs "~1.24x". Those disagree slightly: the nearest factory plot to a corner
 * is 11 away, giving 1.22x, while 1.24x comes from measuring 12 — the factory
 * *centre*. The written rule wins over the derived figure; see SPEC-ERRATA E11.
 */
export function distanceFromFactory(coord: PlotCoord): number {
  const dx = Math.max(0, FACTORY_MIN - coord.x, coord.x - FACTORY_MAX);
  const dy = Math.max(0, FACTORY_MIN - coord.y, coord.y - FACTORY_MAX);
  return Math.max(dx, dy);
}

/**
 * Build time for a plot. spec §9:
 *
 *   buildTicks = baseTicks * (1 + distanceFromFactory * 0.02)
 *
 * Kept in integer arithmetic — the 0.02 is applied as 2/100 — so no float ever
 * reaches state. Without this scaling, position on the grid is purely cosmetic:
 * with roads guaranteed by adjacency and no pathfinding, nothing else makes one
 * plot different from another.
 */
export function buildTicksFor(coord: PlotCoord): number {
  const percent = 100 + distanceFromFactory(coord) * (BUILD_DISTANCE_PENALTY_PER_PLOT * 100);
  return Math.round((BASE_BUILD_TICKS * percent) / 100);
}

/** Plots currently consuming mech-time, in row-major order. spec §11 rule 3 */
export function activePlots(state: GameState): Plot[] {
  const active: Plot[] = [];
  for (let y = 0; y < CITY_GRID_SIZE; y++) {
    for (let x = 0; x < CITY_GRID_SIZE; x++) {
      const plot = state.city.grid[y]?.[x];
      if (plot == null) continue;
      if (plot.state === 'building' || plot.state === 'demolishing') active.push(plot);
    }
  }
  return active;
}

/**
 * Spread the mech pool across active plots.
 *
 * Every active plot gets at least one — that is what makes the pool a
 * concurrency limit. The remainder is handed out starting at an offset that
 * advances with the tick, so the same plots do not collect the spare mech every
 * tick and finish measurably sooner than their neighbours purely by position.
 */
export function distributeMechs(state: GameState): void {
  const active = activePlots(state);
  state.mechs.assigned = active.length;

  if (active.length === 0) return;

  const base = Math.floor(state.mechs.total / active.length);
  let remainder = state.mechs.total % active.length;
  const offset = state.tick % active.length;

  for (let i = 0; i < active.length; i++) {
    active[i]!.assignedMechs = Math.max(1, base);
  }

  // Only distribute a remainder when there are genuinely surplus mechs; if the
  // pool is smaller than the active set, every plot is already down to its
  // minimum of one.
  if (base >= 1) {
    for (let i = 0; i < active.length && remainder > 0; i++) {
      const index = (offset + i) % active.length;
      active[index]!.assignedMechs += 1;
      remainder -= 1;
    }
  }
}

/** Step 7 — advance construction. spec §8 step 7 */
export function advanceConstruction(state: GameState): void {
  distributeMechs(state);

  for (const plot of activePlots(state)) {
    if (plot.state !== 'building') continue;
    plot.progressTicks += plot.assignedMechs;
    if (plot.progressTicks >= plot.totalTicks) {
      plot.progressTicks = plot.totalTicks;
      plot.state = 'complete';
      plot.assignedMechs = 0;
      // Completed cold. Nothing runs, and nothing draws upkeep, until the
      // player switches it on. See Plot.enabled.
      plot.enabled = false;
    }
  }
}

/**
 * Step 8 — advance demolition. spec §8 step 8
 *
 * Mechs were already allocated in step 7; demolition competes for the same pool
 * at the same rate as construction, which is spec §9's only irreversible cost in
 * the city layer.
 */
export function advanceDemolition(state: GameState): void {
  for (let y = 0; y < CITY_GRID_SIZE; y++) {
    for (let x = 0; x < CITY_GRID_SIZE; x++) {
      const plot = state.city.grid[y]?.[x];
      if (plot == null || plot.state !== 'demolishing') continue;

      plot.progressTicks += plot.assignedMechs;
      if (plot.progressTicks < plot.totalTicks) continue;

      // 100% material refund on completion. spec §9
      const cost = BUILDINGS[plot.buildingType].cost;
      for (const id of GOOD_IDS) {
        const amount = cost[id];
        if (amount === undefined) continue;
        state.inventory.goods[id] += amount;
      }
      state.city.grid[y]![x] = null;
    }
  }
}
